import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import Alert from '@mui/material/Alert';
import Modal from '../Modal';
import Input from '../Input';
import Button from '../Button';
import Toast from '../Toast';
import ComplianceService from '../../services/compliance.service';

interface ErasureConfirmModalProps {
  open: boolean;
  dsarId: number;
  subjectEmail: string;
  onClose: () => void;
  onErased: () => void;
}

/** US-054 AC-2/AC-5: lists fields to be anonymised, requires an explicit reason, warns this cannot be undone. */
const ErasureConfirmModal: React.FC<ErasureConfirmModalProps> = ({ open, dsarId, subjectEmail, onClose, onErased }) => {
  const { t } = useTranslation();
  const [reason, setReason] = useState('');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (open) setReason('');
  }, [open]);

  const handleConfirm = useCallback(async () => {
    setSubmitting(true);
    try {
      await ComplianceService.erase(dsarId, reason);
      toast.success(<Toast message={t('compliance.dsar.pageTexts.erasure.success')} />, { icon: false });
      onErased();
    } finally {
      setSubmitting(false);
    }
  }, [dsarId, reason, onErased, t]);

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={t('compliance.dsar.pageTexts.erasure.title')}
      content={
        <div>
          <Alert severity="warning" className="mb-3">
            {t('compliance.dsar.pageTexts.erasure.fieldsWarning')}
          </Alert>
          <Alert severity="error" className="mb-3">
            {t('compliance.dsar.pageTexts.erasure.cannotUndo')}
          </Alert>
          <div className="mb-2 text-muted small">{subjectEmail}</div>
          <Input
            id="erasureReason"
            name="erasureReason"
            label={t('compliance.dsar.pageTexts.erasure.reasonLabel')}
            required
            value={reason}
            onChange={(e) => setReason(e.target.value)}
          />
        </div>
      }
      action={
        <>
          <Button className="btn btn-outline-secondary" text={t('compliance.dsar.pageTexts.cancel')} onClick={onClose} />
          <Button
            className="btn btn-danger"
            text={t('compliance.dsar.pageTexts.erasure.confirm')}
            loading={submitting}
            disabled={!reason.trim()}
            onClick={handleConfirm}
          />
        </>
      }
    />
  );
};

export default ErasureConfirmModal;
